import { ServiceTokenResponse } from './types';

/**
 * Keep refused the agent's credentials outright (401): the pairing was
 * revoked/decommissioned or the secret rotated out from under this
 * machine. Distinct from transient exchange failures so a paired binary
 * can exit with a dedicated code and a re-pair instruction instead of
 * retrying forever.
 */
export class AgentCredentialRevokedError extends Error {
  readonly code = 'PAIRING_REVOKED';

  constructor(message: string) {
    super(message);
    this.name = 'AgentCredentialRevokedError';
  }
}

/**
 * Agent-credentialed token manager. Mirrors ServiceTokenManager but
 * exchanges against `/api/agent-tokens`, which validates against the
 * `agents` table. Agents and service identities use the same
 * credential shape (clientId + clientSecret) but are persisted in
 * different tables with different ownership semantics:
 *
 *  - Service identities are server-to-server daemons (whittle-sift,
 *    whittle-loom). They authenticate at app boot from a long-lived
 *    secret kept in Helm/Infisical.
 *  - Agents are user-paired binaries (Sifter, future whitelabels).
 *    They authenticate from credentials issued at pair time and
 *    stored on the user's machine.
 *
 * Substrate consumers (Shuttle, the Sifter brand) use this manager;
 * the daemon services keep using ServiceTokenManager.
 */
export interface AgentTokenManagerConfig {
  keepApiUrl: string;
  clientId: string;
  clientSecret: string;
  /** See ServiceTokenManagerConfig.onResponse — same min-version contract. */
  onResponse?: (response: Pick<Response, 'headers' | 'status'>) => void;
}

export class AgentTokenManager {
  private token: string | null = null;
  private expiresAt: number = 0;
  private pendingExchange: Promise<string> | null = null;
  private keepApiUrl: string;
  private clientId: string;
  private clientSecret: string;
  private enabled: boolean;
  private onResponse?: (response: Pick<Response, 'headers' | 'status'>) => void;

  constructor(config: AgentTokenManagerConfig) {
    this.keepApiUrl = config.keepApiUrl.replace(/\/$/, '');
    this.clientId = config.clientId;
    this.clientSecret = config.clientSecret;
    this.enabled = !!(config.clientId && config.clientSecret && config.keepApiUrl);
    this.onResponse = config.onResponse;
  }

  async getToken(forceRefresh = false): Promise<string | null> {
    if (!this.enabled) return null;

    // `forceRefresh` skips the cache to re-mint after a server-side rejection
    // (the JobsClient calls this on a 401), recovering from invalidations the
    // local expiry clock can't see.
    if (!forceRefresh && this.token && Date.now() < this.expiresAt - 30_000) {
      return this.token;
    }

    if (this.pendingExchange) {
      return this.pendingExchange;
    }

    this.pendingExchange = this.exchange();
    try {
      return await this.pendingExchange;
    } finally {
      this.pendingExchange = null;
    }
  }

  private async exchange(): Promise<string> {
    const url = `${this.keepApiUrl}/api/agent-tokens`;

    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        clientId: this.clientId,
        clientSecret: this.clientSecret,
      }),
    });
    this.onResponse?.(response);

    const json = (await response.json()) as {
      success: boolean;
      data: ServiceTokenResponse;
      error?: { code: string; message: string };
    };

    if (!response.ok || !json.success) {
      const msg = json.error?.message ?? `Agent token exchange failed with status ${response.status}`;
      if (response.status === 401) {
        throw new AgentCredentialRevokedError(
          `[AgentTokenManager] Keep refused this agent's credentials (401): the pairing was revoked or the secret rotated. Re-pair to continue.`,
        );
      }
      throw new Error(`[AgentTokenManager] ${msg}`);
    }

    this.token = json.data.token;
    this.expiresAt = Date.now() + json.data.expiresIn * 1000;

    return this.token;
  }
}
